import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { Sync as RefreshIcon } from '@mui/icons-material';
import { jobSourcesApi } from '../../services/api';
import { IngestRunResult, JobSourcesOverview } from '../../types';
import { errorMessage } from '../../utils/errors';
import {
  EMPTY_RUN_PROGRESS,
  RunProgress,
  activeBoardsToRead,
  addRunResult,
  countLabel,
  durationLabel,
  formatCount,
  runShouldContinue,
} from '../../utils/jobSources';
import ConfirmDialog from '../common/ConfirmDialog';

type Mode = 'due' | 'all';
type Status = 'idle' | 'running' | 'done' | 'stopped' | 'error';

interface Props {
  overview: JobSourcesOverview;
  /** Called after every call that was answered (or failed), so the page can re-read its numbers. */
  onProgress: () => void;
}

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="dt">
      {label}
    </Typography>
    <Typography variant="subtitle1" component="dd" sx={{ m: 0, fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
);

/**
 * Refresh by hand. One call to the API works for up to about 40 seconds and
 * says how many boards are left; this keeps calling until none are, adding up
 * what each call reports.
 */
const RefreshNow: React.FC<Props> = ({ overview, onProgress }) => {
  const [status, setStatus] = useState<Status>('idle');
  const [mode, setMode] = useState<Mode>('due');
  const [progress, setProgress] = useState<RunProgress>(EMPTY_RUN_PROGRESS);
  const [error, setError] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);

  // The loop outlives renders: it reads and writes these, not state.
  const stopRef = useRef(false);
  const aliveRef = useRef(true);
  // `before` is the moment a "refresh everything" sweep began. The API returns
  // it on the first call and continues the same sweep when it is sent back.
  const runRef = useRef<{ mode: Mode; progress: RunProgress; before: string | null }>({
    mode: 'due',
    progress: EMPTY_RUN_PROGRESS,
    before: null,
  });
  const latest = useRef({ overview, onProgress });
  latest.current = { overview, onProgress };

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      // Leaving the page: let the call in flight finish and make no more.
      aliveRef.current = false;
      stopRef.current = true;
    };
  }, []);

  const loop = useCallback(async (nextMode: Mode, resume: boolean) => {
    if (!resume) runRef.current = { mode: nextMode, progress: EMPTY_RUN_PROGRESS, before: null };
    const run = runRef.current;
    stopRef.current = false;
    setMode(run.mode);
    setProgress(run.progress);
    setError(null);
    setStopping(false);
    setStatus('running');

    for (;;) {
      let result: IngestRunResult;
      try {
        result = await jobSourcesApi.run(run.mode === 'all' ? { all: true, ...(run.before ? { before: run.before } : {}) } : {});
      } catch (e) {
        if (!aliveRef.current) return;
        // Keep what the earlier calls reported; only this call is lost.
        setError(errorMessage(e, 'The refresh call failed.'));
        setStatus('error');
        latest.current.onProgress();
        return;
      }
      if (!aliveRef.current) return;
      if (run.mode === 'all' && !run.before) run.before = result.before ?? null;
      run.progress = addRunResult(run.progress, result);
      setProgress(run.progress);
      latest.current.onProgress();
      if (!runShouldContinue(run.progress, result)) {
        setStatus('done');
        return;
      }
      if (stopRef.current) {
        setStatus('stopped');
        return;
      }
    }
  }, []);

  const running = status === 'running';
  const { totals, settings } = overview;
  const remaining = progress.remaining;
  const total = remaining === null ? null : progress.boards + remaining;
  const percent = total ? Math.min(100, Math.round((progress.boards / total) * 100)) : null;
  const canContinue = (status === 'stopped' || status === 'error') && (remaining === null || remaining > 0);
  const everythingCount = activeBoardsToRead(overview);

  const stop = () => {
    stopRef.current = true;
    setStopping(true);
  };

  let headline = '';
  if (running) {
    headline = stopping
      ? 'Stopping after the current call...'
      : `${mode === 'all' ? 'Refreshing every board' : 'Refreshing due boards'}: call ${formatCount(
          progress.calls + 1
        )} in progress. Each call takes up to about 40 seconds.`;
  } else if (status === 'done') {
    headline =
      progress.boards === 0 && progress.deferred > 0
        ? 'Nothing to read here: the boards that are due are read by the scheduled refresh.'
        : progress.boards === 0
        ? mode === 'all'
          ? 'Nothing to refresh: no board is switched on.'
          : 'Nothing to refresh: no board is due.'
        : remaining
        ? `Finished with ${countLabel(remaining, 'board')} still to read.`
        : `Finished. ${countLabel(progress.boards, 'board')} read in ${durationLabel(progress.seconds)}.`;
  } else if (status === 'stopped') {
    headline = `Stopped with ${countLabel(remaining ?? 0, 'board')} still to read.`;
  } else if (status === 'error') {
    headline = 'Stopped by an error. The numbers below are what was done before it.';
  }

  return (
    <Paper variant="outlined" component="section" aria-labelledby="sources-run-title" sx={{ p: 2 }}>
      <Typography id="sources-run-title" variant="h6" component="h2" sx={{ fontWeight: 700 }}>
        Refresh now
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {settings.paused
          ? 'Reads the boards that are due, oldest first. This works while scheduled refreshing is paused.'
          : `Reads the boards that are due, oldest first. ${countLabel(totals.due_boards, 'board')} ${
              totals.due_boards === 1 ? 'is' : 'are'
            } due right now.`}
      </Typography>

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          disabled={running}
          onClick={() => loop('due', false)}
        >
          Refresh due boards
        </Button>
        <Button variant="outlined" disabled={running} onClick={() => setConfirmAll(true)}>
          Refresh everything
        </Button>
        {running && (
          <Button color="warning" onClick={stop} disabled={stopping}>
            {stopping ? 'Stopping...' : 'Stop after this call'}
          </Button>
        )}
        {canContinue && (
          <Button onClick={() => loop(runRef.current.mode, true)}>Continue</Button>
        )}
      </Stack>

      {status !== 'idle' && (
        <Box sx={{ mt: 2 }}>
          {running && (
            <LinearProgress
              aria-label="Refresh progress"
              variant={percent === null ? 'indeterminate' : 'buffer'}
              value={percent ?? undefined}
              valueBuffer={percent ?? undefined}
              sx={{ mb: 1 }}
            />
          )}
          <Typography variant="body2" role="status" aria-live="polite" sx={{ fontWeight: 600, mb: 1 }}>
            {headline}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}
          {progress.deferred > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {countLabel(progress.deferred, 'board')} with a slow feed (Jobvite) {progress.deferred === 1 ? 'was' : 'were'} left for
              the scheduled refresh, which has the time to read {progress.deferred === 1 ? 'it' : 'them'}.
            </Typography>
          )}
          <Box
            component="dl"
            aria-label="Refresh totals"
            sx={{
              m: 0,
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: 1,
            }}
          >
            <Stat label="Boards read" value={formatCount(progress.boards)} />
            <Stat label="Boards remaining" value={remaining === null ? 'Not known yet' : formatCount(remaining)} />
            <Stat label="Boards failed" value={formatCount(progress.failed)} />
            <Stat label="Jobs created" value={formatCount(progress.created)} />
            <Stat label="Jobs updated" value={formatCount(progress.updated)} />
            <Stat label="Jobs retired" value={formatCount(progress.deactivated)} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {countLabel(progress.calls, 'call')} answered
            {progress.seconds > 0 ? `, ${durationLabel(progress.seconds)} of reading` : ''}.
          </Typography>
        </Box>
      )}

      <ConfirmDialog
        open={confirmAll}
        title="Refresh everything?"
        message={`This re-reads all ${countLabel(everythingCount, 'active board')}, not only the ones that are due. It takes several minutes, and this page has to stay open until it finishes. You can stop it between calls.`}
        confirmLabel="Refresh everything"
        confirmColor="warning"
        onCancel={() => setConfirmAll(false)}
        onConfirm={() => {
          setConfirmAll(false);
          loop('all', false);
        }}
      />
    </Paper>
  );
};

export default RefreshNow;
